import { browser } from '$app/environment';
import { v4 as uuidv4 } from 'uuid';
import { COOKIES, LOCAL_STORAGE_KEYS, QUERY_PARAM_KEYS } from './constants';
import { Sentry } from '$lib/services/sentry';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { setMetaStore } from '$lib/stores/app/store';
import { LocalStorageStore } from './local-storage-store';

let deviceIdFallbackUsed = false;

// Deferred event queue mechanism (scrappy, local solution)
// TODO: remove this once we have refactored the device id logic away from cookies - AD
let isDeferralActive = false;
/** @type {string | null} */
let cachedInMemoryDeviceId = null;
/** @type {Array<() => Promise<void|object>>} */
const deferredEventsQueue = [];

/**
 * Wrapper for AnalyticsService.statelessEventLog that queues events when deferral is active.
 * @param {object} payload - The payload to log
 * @param {string} deviceId - The device id to log
 * @param {boolean} preserveSessionDeviceId - Whether to preserve the session device id
 * @returns {Promise<void|object>} - A promise that resolves when the event is logged (or queued)
 */
export const deferredStatelessEventLog = async (payload, deviceId, preserveSessionDeviceId) => {
	if (isDeferralActive) {
		deferredEventsQueue.push(() =>
			AnalyticsService.statelessEventLog(payload, deviceId, preserveSessionDeviceId)
		);
		return Promise.resolve();
	} else {
		return AnalyticsService.statelessEventLog(payload, deviceId, preserveSessionDeviceId);
	}
};

/**
 * Enables event deferral - events will be queued instead of firing immediately.
 * @returns {void}
 */
export const enableEventDeferral = () => {
	isDeferralActive = true;
};

/**
 * Disables event deferral - events will fire immediately.
 * @returns {void}
 */
export const disableEventDeferral = () => {
	isDeferralActive = false;
};

/**
 * Flushes all deferred events, executing them in order.
 * @returns {Promise<void>}
 */
export const flushDeferredEvents = async () => {
	const events = [...deferredEventsQueue];
	deferredEventsQueue.length = 0;

	for (const eventFn of events) {
		try {
			await eventFn();
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error flushing deferred device ID event',
				},
			});
		}
	}
};

export const localStorageStore = new LocalStorageStore();

// Cross-tab cache invalidation. Without this, Tab A's cachedInMemoryDeviceId
// never sees a write made by Tab B and keeps serializing the stale value into
// every API call. The cache itself stays (it's load-bearing for iOS Safari,
// see ABC2-13445); we just keep it in sync.
if (typeof window !== 'undefined') {
	window.addEventListener('storage', (e) => {
		if (e.key !== LOCAL_STORAGE_KEYS.DEVICE_ID) return;
		if (!e.newValue || e.newValue === 'null') return;
		if (e.newValue === cachedInMemoryDeviceId) return;
		cachedInMemoryDeviceId = e.newValue;
		setSentryDeviceId(e.newValue);
		syncMetaDeviceId(e.newValue);
	});
}

// Dev/test-only escape hatch so e2e specs can read this tab's in-memory cache
// independently from localStorage (which is shared across tabs). Tree-shaken
// out of production builds via `import.meta.env.DEV`.
if (typeof window !== 'undefined' && import.meta.env.DEV) {
	/** @type {any} */ (window).__deviceIdCacheForTesting = () => cachedInMemoryDeviceId;
}

export const migrateDeviceIdAndFixErroneousValues = () => {
	let cookiedDeviceId = Cookies.get(COOKIES.DEVICE_ID);
	let localStorageDeviceId = localStorageStore.get(LOCAL_STORAGE_KEYS.DEVICE_ID);
	const localStorageDeviceIdBackup = localStorageStore.get(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP);

	if (cookiedDeviceId === 'null') {
		Sentry.addBreadcrumb({
			category: 'device_id_migration',
			message: 'Removing erroneous (null) device ID from cookies',
		});
		Cookies.remove(COOKIES.DEVICE_ID);
		cookiedDeviceId = undefined;
	}

	if (localStorageDeviceId === 'null') {
		Sentry.addBreadcrumb({
			category: 'device_id_migration',
			message: 'Removing erroneous (null) device ID from localStorage',
		});
		localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
		localStorageDeviceId = null;
	}

	if (cookiedDeviceId) {
		Sentry.addBreadcrumb({
			category: 'device_id_migration',
			message: 'Migrating (non-null) device ID from cookies to localStorage',
		});
		Cookies.remove(COOKIES.DEVICE_ID);
		saveDeviceId(cookiedDeviceId);
	}

	if (localStorageDeviceIdBackup) {
		localStorageStore.remove(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP);
	}
};

export const DEVICE_ID_FAILURE_TYPES = {
	fallback: 'device_id_fallback',
	init_failure: 'device_id_init_failure',
	get_failure: 'device_id_get_failure',
};

/**
 * Fires a device ID failure event.
 * @param {string} failureType - The type of failure that occurred
 * @param {string} backupDeviceId - The backup device ID to fire the event for
 * @returns {void} The device ID failure event, or null if not initialized
 */
export const fireDeviceIdFailureEvent = (
	failureType,
	backupDeviceId = 'client_failure:fireDeviceIdFailureEvent:default'
) => {
	switch (failureType) {
		case DEVICE_ID_FAILURE_TYPES.fallback:
			if (deviceIdFallbackUsed) return;
			else {
				deviceIdFallbackUsed = true;
				deferredStatelessEventLog(
					{
						event_subtype: DEVICE_ID_FAILURE_TYPES.fallback,
						browser,
						window: typeof window !== 'undefined',
					},
					backupDeviceId
				).catch((err) =>
					Sentry.captureException(err, {
						extra: {
							action: 'getDeviceId fallback error',
						},
					})
				);
			}
			break;
		default:
			deferredStatelessEventLog(
				{
					event_subtype: failureType,
					browser,
					window: typeof window !== 'undefined',
				},
				backupDeviceId
			);
			break;
	}
};

/**
 * Gets an existing device ID from cookies or in-memory storage.
 * @returns {string | null} The existing device ID, or null if not found
 */
export const getDeviceId = () => {
	if (typeof window !== 'undefined') {
		if (cachedInMemoryDeviceId) return cachedInMemoryDeviceId;

		const deviceId = localStorageStore.get(LOCAL_STORAGE_KEYS.DEVICE_ID);

		cachedInMemoryDeviceId = deviceId;
		return deviceId;
	} else return null;
};

/**
 * Sets Sentry tags and user context for a device ID.
 * This should be called after the device ID is saved to cookies.
 * @param {string} deviceId - The device ID to set in Sentry
 */
const setSentryDeviceId = (deviceId) => {
	Sentry.setTag(COOKIES.DEVICE_ID, deviceId);
	Sentry.setUser({ id: deviceId });
};

/**
 * Keeps app.meta.deviceId aligned with the canonical device ID store.
 * @param {string} deviceId - The device ID to mirror into the app meta store
 * @returns {void}
 */
const syncMetaDeviceId = (deviceId) => {
	if (typeof window === 'undefined') return;

	void setMetaStore({ deviceId }).catch((err) => {
		Sentry.captureException(err, {
			extra: {
				action: 'syncMetaDeviceId failed',
			},
		});
	});
};

/**
 * Creates a new device ID and saves it to cookies.
 * Also sets Sentry tags and user context.
 * @returns {boolean} whether the device ID was saved successfully
 */
const createAndSaveDeviceId = () => {
	const deviceId = uuidv4();
	return saveDeviceId(deviceId);
};

/**
 * Saves a device ID to cookies.
 * Supports only browser (uses js-cookie).
 * Also sets Sentry tags and user context after saving.
 * @param {string} deviceId - The device ID to save
 * @returns {boolean} whether the device ID was saved successfully
 */
export const saveDeviceId = (deviceId) => {
	console.assert(deviceId, 'Device ID is either not required or not truthy');
	if (!deviceId) return false;

	localStorageStore.set(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);

	cachedInMemoryDeviceId = deviceId;

	setSentryDeviceId(deviceId);
	syncMetaDeviceId(deviceId);
	return getDeviceId() === deviceId;
};

/**
 * Initializes the device ID once per web app load.
 * This should be called early in the app lifecycle (e.g., in +layout.js).
 * Behavior:
 * - Checks URL params first (if found, saves it to cookies)
 * - Checks cookies for existing device ID
 * - Creates and saves a new device ID if none exists
 * Use getDeviceId() to read the device ID after initialization.
 * @returns {Promise<boolean>} True if the device ID was initialized as defined above, false if not
 */
export const initializeDeviceId = async () => {
	if (window === undefined) return false;

	const existingDeviceId = getDeviceId();
	const searchParams = new URLSearchParams(window.location.search);

	if (searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID)) {
		const deviceIdFromUrl = searchParams.get(QUERY_PARAM_KEYS.DEVICE_ID) || '';

		if (deviceIdFromUrl && deviceIdFromUrl !== 'null' && deviceIdFromUrl !== existingDeviceId) {
			saveDeviceId(deviceIdFromUrl);
			await deferredStatelessEventLog(
				{
					event_subtype: 'device_id_update',
					source: 'url_param',
					initial_device_id: existingDeviceId,
					new_device_id: deviceIdFromUrl,
				},
				deviceIdFromUrl,
				false
			);
			return true;
		}
	}

	if (existingDeviceId) {
		setSentryDeviceId(existingDeviceId);
		return true;
	}

	createAndSaveDeviceId();
	return true;
};
